// src/services/SessionService.js
import auditService from './AuditService';
import storage from './StorageAdapter';
import { getCurrentLanguage } from '../useTranslations';

class SessionService {
  constructor() {
    this.STORAGE_KEY = 'rms_session';
    this.ACTIVITY_KEY = 'rms_last_activity';

    // In-memory cache for isValidSession() — stays sync by reading from cache.
    // Populated by createSession() and refreshSession(); cleared by destroySession().
    this._sessionCache = null;
    this._activityCache = null;

    this.activityTimer = null;
    this.setupActivityTracking();
  }

  // Hämta dynamiska inställningar
  getSettings() {
    const parsed = storage.getJSON('rms_settings', {});
    return parsed.session || this.getDefaultSettings();
  }

  // Default inställningar
  getDefaultSettings() {
    return {
      sessionTimeout: 8 * 60 * 60 * 1000,        // 8 timmar
      inactivityTimeout: 30 * 60 * 1000,         // 30 minuter
      warningTime: 5 * 60 * 1000,                // 5 minuter varning
      inactivityWarningTime: 2 * 60 * 1000,      // 2 minuter inaktivitetsvarning
      activityCheckInterval: 60 * 1000           // 1 minut kontrollintervall
    };
  }

  // Uppdatera inställningar (anropas från SettingsManager)
  async updateSettings(newSettings) {
    // Uppdatera befintlig session med nya timeout
    const session = this.getSession();
    if (session && session.authenticated) {
      const now = Date.now();
      const timeSinceLogin = now - session.loginTime;

      // Beräkna ny expires baserat på nya timeout
      session.expires = session.loginTime + newSettings.sessionTimeout;

      // Om nya timeout är kortare än tiden sedan login, sätt expires till nu + grace period
      if (timeSinceLogin > newSettings.sessionTimeout) {
        session.expires = now + (5 * 60 * 1000); // 5 minuters grace period
      }

      // Spara uppdaterad session
      await storage.setJSON(this.STORAGE_KEY, session);
      this._sessionCache = session;
    }

    // Starta om inaktivitetsövervakning med nya inställningar
    this.startInactivityMonitoring();

    // Trigga omedelbar uppdatering av UI
    this.notifyUIUpdate();
  }

  // Notifiera UI om uppdateringar (för TopMenu)
  notifyUIUpdate() {
    // Dispatcha custom event för UI-uppdateringar
    const event = new CustomEvent('sessionSettingsUpdated', {
      detail: { settings: this.getSettings() }
    });
    window.dispatchEvent(event);
  }

  // Skapa ny session (vid login)
  // token: JWT-token från backend (null för lokal auth)
  async createSession(username, token = null) {
    const now = Date.now();
    const settings = this.getSettings();

    const sessionData = {
      username,
      loginTime: now,
      lastActivity: now,
      expires: now + settings.sessionTimeout,
      authenticated: true,
      token: token || null
    };

    // Update cache BEFORE awaits so isValidSession() sees it immediately
    this._sessionCache = sessionData;
    this._activityCache = now;

    await storage.setJSON(this.STORAGE_KEY, sessionData);
    await storage.setItem(this.ACTIVITY_KEY, now.toString());

    // Starta inaktivitetsövervakning
    this.startInactivityMonitoring();

    return sessionData;
  }

  // Ladda om session från storage och uppdatera cache.
  // Anropas vid app-mount för att säkerställa att cachen är varm.
  async refreshSession() {
    try {
      const session = storage.getJSON(this.STORAGE_KEY, null);
      const activityRaw = storage.getItem(this.ACTIVITY_KEY);
      if (session && session.authenticated) {
        this._sessionCache = session;
        this._activityCache = activityRaw ? parseInt(activityRaw, 10) : null;
      } else {
        this._sessionCache = null;
        this._activityCache = null;
      }
    } catch (_e) {
      this._sessionCache = null;
      this._activityCache = null;
    }
  }

  // Kontrollera om session är giltig.
  // SYNC — måste förbli sync eftersom den används i App.js useState-initialisering.
  // Läser från minnescache om tillgänglig; faller tillbaka på synkron storage-läsning.
  isValidSession() {
    const settings = this.getSettings();
    const now = Date.now();

    // Prefer cache
    if (this._sessionCache !== null) {
      if (!this._sessionCache.authenticated) return false;

      if (now > this._sessionCache.expires) {
        this.destroySession('SESSION_EXPIRED');
        return false;
      }

      if (this._activityCache !== null) {
        if (now - this._activityCache > settings.inactivityTimeout) {
          this.destroySession('INACTIVITY_TIMEOUT');
          return false;
        }
      }

      return true;
    }

    // Fallback: sync storage read (backward compat — used before cache is hydrated)
    const session = this.getSession();
    if (!session) return false;

    if (now > session.expires) {
      this.destroySession('SESSION_EXPIRED');
      return false;
    }

    const lastActivity = parseInt(storage.getItem(this.ACTIVITY_KEY) || '0');
    if (now - lastActivity > settings.inactivityTimeout) {
      this.destroySession('INACTIVITY_TIMEOUT');
      return false;
    }

    return session.authenticated;
  }

  // Hämta JWT-token för aktuell session (null om lokal auth)
  getToken() {
    const session = this.getSession();
    return session ? session.token || null : null;
  }

  // Kontrollera om session har ett JWT-token
  hasToken() {
    return this.getToken() !== null;
  }

  // Hämta session data (sync — reads from storage directly)
  getSession() {
    try {
      return storage.getJSON(this.STORAGE_KEY, null);
    } catch (error) {
      console.error('[SESSION] Error parsing session data:', error);
      storage.removeItem(this.STORAGE_KEY);
      storage.removeItem(this.ACTIVITY_KEY);
      return null;
    }
  }

  // Uppdatera last activity
  async updateActivity() {
    const now = Date.now();
    const session = this.getSession();

    if (session && this.isValidSession()) {
      session.lastActivity = now;
      this._activityCache = now;
      await storage.setJSON(this.STORAGE_KEY, session);
      await storage.setItem(this.ACTIVITY_KEY, now.toString());
    }
  }

  // Förläng session (om användaren är aktiv)
  async extendSession() {
    const session = this.getSession();
    if (session && this.isValidSession()) {
      const now = Date.now();
      const settings = this.getSettings();

      session.expires = now + settings.sessionTimeout;
      session.lastActivity = now;

      this._sessionCache = session;
      this._activityCache = now;

      await storage.setJSON(this.STORAGE_KEY, session);
      await storage.setItem(this.ACTIVITY_KEY, now.toString());
    }
  }

  // Förstör session
  async destroySession(reason = 'MANUAL_LOGOUT') {
    const session = this.getSession();

    if (session) {
      // Logga logout med audit service
      auditService.log('LOGOUT', 'Auth', {
        username: session.username,
        reason,
        sessionDuration: Date.now() - session.loginTime
      });
    }

    // Rensa cache omedelbart
    this._sessionCache = null;
    this._activityCache = null;

    // Rensa all session data
    await storage.removeItem(this.STORAGE_KEY);
    await storage.removeItem(this.ACTIVITY_KEY);
    await storage.removeItem('authenticated'); // Gammal nyckel (bakåtkompatibilitet)
    storage.clearCache();

    // Stoppa inaktivitetsövervakning
    this.stopInactivityMonitoring();
  }

  // Sätt upp aktivitetsspårning
  setupActivityTracking() {
    // Spåra användaraktivitet
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, () => {
        if (this.isValidSession()) {
          this.updateActivity();
        }
      }, { passive: true });
    });
  }

  // Starta inaktivitetsövervakning
  startInactivityMonitoring() {
    this.stopInactivityMonitoring(); // Stoppa eventuell befintlig timer

    const settings = this.getSettings();

    this.activityTimer = setInterval(() => {
      if (!this.isValidSession()) {
        // Session ogiltig - meddela React-appen att navigera till login
        window.dispatchEvent(new CustomEvent('sessionExpired'));
      }
    }, settings.activityCheckInterval);
  }

  // Stoppa inaktivitetsövervakning
  stopInactivityMonitoring() {
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
  }

  // Hämta session info för visning
  getSessionInfo() {
    const session = this.getSession();
    if (!session) return null;

    const now = Date.now();
    const timeLeft = session.expires - now;
    const inactivityTime = now - parseInt(storage.getItem(this.ACTIVITY_KEY) || '0');

    return {
      username: session.username,
      loginTime: new Date(session.loginTime).toLocaleString(getCurrentLanguage() === 'sv' ? 'sv-SE' : 'en-GB'),
      timeLeft: Math.max(0, timeLeft),
      timeLeftComponents: this.getTimeComponents(Math.max(0, timeLeft)),
      inactivityTime: inactivityTime,
      inactivityTimeComponents: this.getTimeComponents(inactivityTime),
      isActive: inactivityTime < this.INACTIVITY_TIMEOUT
    };
  }

  // Returnera tidskomponenter (dagar, timmar, minuter) för UI-formatering via t()
  getTimeComponents(milliseconds) {
    if (milliseconds <= 0) return { days: 0, hours: 0, minutes: 0 };

    const totalMinutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const days = Math.floor(hours / 24);

    return {
      days,
      hours: hours % 24,
      minutes: totalMinutes % 60
    };
  }

  // Formatera tid till läsbar format (fallback, ej för UI-visning)
  formatTime(milliseconds) {
    const { days, hours, minutes } = this.getTimeComponents(milliseconds);
    if (days === 0 && hours === 0 && minutes === 0) return '0 minutes';

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${hours !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  }

  // Visa session varning (när session snart går ut)
  checkSessionWarning() {
    const session = this.getSession();
    if (!session) return false;

    const now = Date.now();
    const settings = this.getSettings();
    const timeLeft = session.expires - now;
    const inactivityTime = now - parseInt(storage.getItem(this.ACTIVITY_KEY) || '0');

    // Varna före session timeout
    if (timeLeft < settings.warningTime && timeLeft > 0) {
      return {
        type: 'SESSION_EXPIRING',
        timeLeft,
        timeComponents: this.getTimeComponents(timeLeft)
      };
    }

    // Varna före inaktivitetstimeout
    const inactivityTimeLeft = settings.inactivityTimeout - inactivityTime;
    if (inactivityTimeLeft < settings.inactivityWarningTime && inactivityTime < settings.inactivityTimeout) {
      return {
        type: 'INACTIVITY_WARNING',
        timeLeft: inactivityTimeLeft,
        timeComponents: this.getTimeComponents(inactivityTimeLeft)
      };
    }

    return false;
  }

  // Migrera från gammalt localStorage system (sync — används i App.js checkAuth)
  migrateOldSession() {
    const oldAuth = storage.getItem('authenticated');
    if (oldAuth === 'true') {
      storage.removeItem('authenticated');

      // Skapa ny session för befintlig användare (sync path via cache)
      const now = Date.now();
      const settings = this.getSettings();
      const sessionData = {
        username: 'simon',
        loginTime: now,
        lastActivity: now,
        expires: now + settings.sessionTimeout,
        authenticated: true,
        token: null
      };
      this._sessionCache = sessionData;
      this._activityCache = now;
      storage.setJSON(this.STORAGE_KEY, sessionData);
      storage.setItem(this.ACTIVITY_KEY, now.toString());
      this.startInactivityMonitoring();
      return true;
    }
    return false;
  }
}

// Singleton instance
const sessionService = new SessionService();

if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.sessionService = sessionService;
}

export default sessionService;
